import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { StackedTokenIcons } from "~/components/stacked-token-icons";
import { TokenIcon } from "~/components/token-icon";
import { m } from "~/lib/i18n";
import type { ExecutionDetail } from "../types";
import {
  ExecutionToastTimeline,
  FallbackTimeline,
  type ToastTimelineEvent,
} from "./execution-toast-timeline";
import type { ExecutionToastView } from "./toast-view-model";

interface ExecutionToastModalProps {
  detail: ExecutionDetail | null;
  /** Run-DO live frames — the realtime timeline source when connected. */
  liveEvents: readonly ToastTimelineEvent[];
  onDismissOperation: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  view: ExecutionToastView | null;
}

/**
 * The expanded center toast (v1's TransactionNotification modal mode): large
 * asset icon, the same tensed headline, the live persisted timeline (fully
 * populated for worker ops — polled while open and non-terminal), an error
 * box, and a link into the portfolio's operation view.
 */
export function ExecutionToastModal({
  detail,
  liveEvents,
  onDismissOperation,
  onOpenChange,
  open,
  view,
}: ExecutionToastModalProps) {
  // Live run-DO frames win while flowing (worker ops); the persisted detail
  // timeline covers hydration and legacy-engine ops.
  const timeline: readonly ToastTimelineEvent[] =
    liveEvents.length > 0 ? liveEvents : (detail?.timeline ?? []);
  const isFailure = view?.status === "error" || view?.status === "cancelled";

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent showCloseButton size="sm">
        {view ? (
          <>
            <DialogHeader className="items-center text-center">
              <div className="mb-3 flex justify-center">
                {view.tokenIcons && view.tokenIcons.length > 1 ? (
                  <StackedTokenIcons size={56} tokens={view.tokenIcons} />
                ) : (
                  <TokenIcon
                    alt={view.iconSymbol ?? m.toast_execution_modal_asset_alt()}
                    chainId={view.chainId ?? undefined}
                    disableLink
                    hideChainBadge={view.chainId === null}
                    size={56}
                    src={view.iconUrl ?? undefined}
                  />
                )}
              </div>
              <DialogTitle className="text-center text-title-sm">
                {view.label}
              </DialogTitle>
              {view.detail ? (
                <DialogDescription className="text-center">
                  {view.detail}
                </DialogDescription>
              ) : null}
            </DialogHeader>

            <div className="mt-4">
              {timeline.length > 0 ? (
                <ExecutionToastTimeline events={timeline} />
              ) : (
                <FallbackTimeline status={view.status} />
              )}
            </div>

            <DialogFooter className="flex-col gap-2 sm:flex-col">
              <Button
                className="w-full"
                onClick={() => {
                  onOpenChange(false);
                  // Lazy: the provider mounts above RouterProvider, and a
                  // static ~/router import would drag the route tree into the
                  // always-mounted toast graph.
                  void import("~/router").then(({ router }) =>
                    router.navigate({
                      to: "/portfolio/$portfolioInstanceId",
                      params: { portfolioInstanceId: view.portfolioId },
                      search: { operationId: view.operationId },
                    }),
                  );
                }}
                variant="secondary"
              >
                {m.toast_execution_modal_view_in_portfolio_cta()}
              </Button>
              <Button
                className="w-full"
                onClick={() => {
                  if (isFailure) {
                    onDismissOperation();
                  }
                  onOpenChange(false);
                }}
                variant={isFailure ? "destructive" : "ghost"}
              >
                {isFailure
                  ? m.toast_execution_modal_dismiss_cta()
                  : m.toast_execution_modal_close_cta()}
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
